/**
 * excel-context — reads "what the user is currently looking at" in Excel and
 * exposes it as a HostContextProvider. Injected as ambient runtime context so
 * the assistant resolves "fix this" / "what's wrong here" against the live
 * selection without the user pasting anything. Mirrors the read patterns in
 * excel-tools.ts (get_active_sheet / read_selected_range).
 */
import { HostContextProvider, capText } from "@usable/office-embed";

const MAX_CONTEXT_CHARS = 4000;

async function read(): Promise<string | null> {
  try {
    return await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      sheet.load("name");
      const selection = context.workbook.getSelectedRange();
      selection.load("address,values,rowCount,columnCount");
      await context.sync();

      const lines = [
        "The user is working in Microsoft Excel.",
        `Active sheet: ${sheet.name}`,
        `Current selection: ${selection.address} (${selection.rowCount} rows × ${selection.columnCount} cols)`,
        `Selected values (row-major JSON): ${JSON.stringify(selection.values)}`,
      ];
      return capText(lines.join("\n"), MAX_CONTEXT_CHARS);
    });
  } catch {
    // Never let a context read break the embed — just skip this refresh.
    return null;
  }
}

function subscribe(onChange: () => void): () => void {
  Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, onChange);
  return () => {
    Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, {
      handler: onChange,
    });
  };
}

export const excelContextProvider: HostContextProvider = { read, subscribe };
